import React, { useMemo, useState } from "react";
import { DataTable } from "@/components/ui/DataTable/index";
import { type DataTableColumn } from "@/components/ui/DataTable/index";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Combobox } from "@/components/ui/combobox";
import { ArrowLeft } from "lucide-react";
import moment from "moment-timezone";
import {
  useListDestinationRuns,
  useListDestinationLocations,
  DestinationFetchRunImproved,
} from "@/lib/api/destinationHoursImproved";
import { CommonDestinationTimezone } from "@/lib/utils";
import { useQueryParam } from "@/hooks/use-query-param";

interface LocationDetailViewProps {
  locationId: string;
  onBackClick: () => void;
  onRunSelect?: (run: DestinationFetchRunImproved) => void;
}

const runStatusOptions = [
  { value: "", label: "All Statuses" },
  { value: "running", label: "Running" },
  { value: "completed", label: "Completed" },
  { value: "failed", label: "Failed" },
  { value: "partial", label: "Partial" },
];

export function LocationDetailView({ locationId, onBackClick, onRunSelect }: LocationDetailViewProps) {
  const [selectedServiceCategoryId, setSelectedServiceCategoryId] = useQueryParam("service_category_id", "");
  const [selectedRunStatus, setSelectedRunStatus] = useQueryParam("run_status", "");

  const locationsQuery = useListDestinationLocations();
  const runsQuery = useListDestinationRuns(locationId, {
    service_category_id: selectedServiceCategoryId || undefined,
    run_status: selectedRunStatus || undefined,
    limit: 500,
  });

  const location = useMemo(() => {
    return locationsQuery.data?.data?.find((loc: { id: string }) => loc.id === locationId);
  }, [locationsQuery.data, locationId]);

  const serviceCategoryOptions = useMemo(() => {
    const options = [{ value: "", label: "All Service Categories" }];
    if (location?.serviceCategories) {
      location.serviceCategories.forEach((category: { id: string; service_name: string; service_group: string }) => {
        options.push({
          value: category.id,
          label: category.service_name,
        });
      });
    }
    return options;
  }, [location]);

  const columns = useMemo((): DataTableColumn<DestinationFetchRunImproved>[] => [
    {
      id: "run_started_at",
      accessorKey: "run_started_at",
      header: "Started At",
      cell: (info) => moment.tz(info.getValue() as Date, CommonDestinationTimezone).format("MMM DD, YYYY hh:mm:ss a"),
      minSize: 150,
      maxSize: 180,
      enableSorting: true,
    },
    {
      id: "run_completed_at",
      accessorKey: "run_completed_at",
      header: "Completed At",
      cell: (info) => {
        const date = info.getValue() as Date | null;
        return date ? moment.tz(date, CommonDestinationTimezone).format("MMM DD, YYYY hh:mm:ss a") : "Not completed";
      },
      minSize: 150,
      maxSize: 180,
      enableSorting: true,
    },
    {
      id: "run_status",
      accessorKey: "run_status",
      header: "Status",
      cell: (info) => {
        const status = info.getValue() as string;
        const variant = status === "completed" ? "default" : 
                      status === "failed" ? "destructive" : 
                      status === "running" ? "secondary" : "outline";
        return <Badge variant={variant}>{status}</Badge>;
      },
      minSize: 100,
      maxSize: 120,
      enableSorting: true,
    },
    {
      id: "raw_hour_ids",
      accessorKey: "raw_hour_ids",
      header: "Found Hours",
      cell: (info) => (info.getValue() as string[]).length,
      minSize: 100,
      maxSize: 120,
      enableSorting: true,
    },
    {
      id: "changed_hour_ids",
      accessorKey: "changed_hour_ids",
      header: "Found Changed",
      cell: (info) => (info.getValue() as string[]).length,
      minSize: 120,
      maxSize: 140,
      enableSorting: true,
    },
  ], []);

  const isLoading = locationsQuery.isLoading || runsQuery.isLoading;
  const runs = runsQuery.data?.data || [];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="outline" size="sm" onClick={onBackClick}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Locations
        </Button>
        <div>
          <h2 className="text-2xl font-bold">
            {location?.name || "Loading..."}
          </h2>
          {location?.alias && (
            <p className="text-muted-foreground">Alias: {location.alias}</p>
          )}
        </div>
      </div>

      {/* Location Info Card */}
      {location && (
        <Card>
          <CardHeader>
            <CardTitle>Location Details</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div>
                <span className="font-medium">Status:</span>{" "}
                <Badge variant={location.is_enabled ? "default" : "secondary"}>
                  {location.is_enabled ? "Enabled" : "Disabled"}
                </Badge>
              </div>
              <div>
                <span className="font-medium">Visibility:</span>{" "}
                <Badge variant={location.is_visible ? "default" : "outline"}>
                  {location.is_visible ? "Visible" : "Hidden"}
                </Badge>
              </div>
              <div>
                <span className="font-medium">Service Categories:</span>{" "}
                {location.serviceCategories?.filter((cat: { is_active: boolean }) => cat.is_active).length || 0} active
              </div>
              <div>
                <span className="font-medium">Last Fetched:</span>{" "}
                {location.last_fetched 
                  ? moment.tz(location.last_fetched, CommonDestinationTimezone).format("MMM DD, YYYY hh:mm a")
                  : "Never"
                }
              </div>
              <div>
                <span className="font-medium">Last Parsed:</span>{" "}
                {location.last_parsed 
                  ? moment.tz(location.last_parsed, CommonDestinationTimezone).format("MMM DD, YYYY hh:mm a")
                  : "Never"
                }
              </div>
              <div>
                <span className="font-medium">Last Ingest:</span>{" "}
                {location.last_ingest 
                  ? moment.tz(location.last_ingest, CommonDestinationTimezone).format("MMM DD, YYYY hh:mm a")
                  : "Never"
                }
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <Combobox
          options={serviceCategoryOptions}
          value={selectedServiceCategoryId}
          onValueChange={setSelectedServiceCategoryId}
          placeholder="Filter by service category"
          dwimOnChange
        />
        <Combobox
          options={runStatusOptions}
          value={selectedRunStatus}
          onValueChange={setSelectedRunStatus}
          placeholder="Filter by run status"
          dwimOnChange
        />
      </div>

      {/* Runs Table */}
      <DataTable
        className="shadow-lg"
        stickyHeaders
        tableClassName="max-h-[calc(100vh-400px)] overflow-y-scroll"
        data={runs}
        columns={columns}
        enableSorting
        enableMultiSort
        enableFiltering
        enableColumnVisibility
        enableExport
        filterStyle="select"
        filterMode="internal"
        rowClickable={!!onRunSelect}
        onRowClick={(row) => onRunSelect && onRunSelect(row)}
        emptyMessage={
          isLoading
            ? "Loading runs..."
            : "No fetch runs found for this location"
        }
      />
    </div>
  );
}
